import { ENGINES, engineHarmonics, engineVoice } from "./engine.js";
import type { EngineType } from "./engine.js";

/**
 * Vehicle sounds, synthesised in WebAudio (no samples to download, nothing to license): the
 * engine of the driven car (a band-limited periodic wave at the firing frequency, a half-order
 * rumble, combustion noise, load filter, soft saturation, a rev-limiter flutter, the starter),
 * gear shifts, tyre squeal, wind, the horn, doors, indicators, impacts, and a quieter 3D voice for
 * other drivers' cars near the camera. Every parameter glides (`setTargetAtTime`), so nothing
 * clicks. On by default once the page has had a user gesture (autoplay policy); a master switch
 * and volume live in the settings (`uz.vehicleAudio` in local storage).
 */

export interface DriveSound {
  rpm: number;
  redline: number;
  idle: number;
  throttle: number;
  /** m/s */
  speed: number;
  /** Strongest tyre slide (0 grip .. 1+). */
  slide: number;
  /** Shift counter: a change plays a shift. */
  shifts: number;
  /** A hit this frame (velocity change, m/s). */
  impact: number;
  cls: string;
  grass: boolean;
  indicator: boolean;
}

export interface RemoteSound {
  id: string;
  x: number;
  y: number;
  z: number;
  speed: number;
  cls: string;
}

const KEY = "uz.vehicleAudio";
const TC = 0.045;

interface Settings {
  enabled: boolean;
  volume: number;
}

function readSettings(): Settings {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Settings>;
      return {
        enabled: p.enabled !== false,
        volume: typeof p.volume === "number" ? Math.max(0, Math.min(1, p.volume)) : 0.7,
      };
    }
  } catch {
    // No storage: the defaults.
  }
  return { enabled: true, volume: 0.7 };
}

interface EngineGraph {
  type: EngineType;
  osc: OscillatorNode;
  sub: OscillatorNode;
  noise: AudioBufferSourceNode;
  band: BiquadFilterNode;
  tone: GainNode;
  rumble: GainNode;
  grit: GainNode;
  shaper: WaveShaperNode;
  low: BiquadFilterNode;
  voice: GainNode;
  flutter: GainNode;
  flutterLfo: OscillatorNode;
  flutterDepth: GainNode;
  lfo: OscillatorNode;
  squealSrc: AudioBufferSourceNode;
  squealBand: BiquadFilterNode;
  squeal: GainNode;
  windSrc: AudioBufferSourceNode;
  windLow: BiquadFilterNode;
  wind: GainNode;
  shifts: number;
  started: number;
  indicatorOn: boolean;
  tick: number;
  dipUntil: number;
}

interface RemoteVoice {
  osc: OscillatorNode;
  low: BiquadFilterNode;
  gain: GainNode;
  panner: PannerNode;
  seen: number;
}

class VehicleAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private engine: EngineGraph | null = null;
  private horn: { a: OscillatorNode; b: OscillatorNode; g: GainNode } | null = null;
  private remotes = new Map<string, RemoteVoice>();
  private listeners = new Set<() => void>();
  private installed = false;
  settings: Settings = readSettings();

  subscribe(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  set(patch: Partial<Settings>): void {
    this.settings = { ...this.settings, ...patch };
    try {
      globalThis.localStorage?.setItem(KEY, JSON.stringify(this.settings));
    } catch {
      // Not persisted.
    }
    if (this.master && this.ctx)
      this.master.gain.setTargetAtTime(
        this.settings.enabled ? this.settings.volume : 0,
        this.ctx.currentTime,
        0.05,
      );
    if (!this.settings.enabled) this.stopEngine();
    for (const l of this.listeners) l();
  }

  /** Creates (or resumes) the audio context on the first user gesture. */
  install(): () => void {
    if (this.installed || typeof window === "undefined") return () => undefined;
    this.installed = true;
    const unlock = () => {
      this.context();
      void this.ctx?.resume().catch(() => undefined);
    };
    window.addEventListener("pointerdown", unlock, { capture: true });
    window.addEventListener("keydown", unlock, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlock, { capture: true });
      window.removeEventListener("keydown", unlock, { capture: true });
      this.installed = false;
    };
  }

  /** Whether sound can play now (enabled, context running). */
  get live(): boolean {
    return this.settings.enabled && this.ctx !== null && this.ctx.state === "running";
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx;
    try {
      const Ctx =
        globalThis.AudioContext ??
        (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -12;
      comp.ratio.value = 4;
      const master = ctx.createGain();
      master.gain.value = this.settings.enabled ? this.settings.volume : 0;
      master.connect(comp).connect(ctx.destination);
      const buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const d = buf.getChannelData(0);
      let seed = 1234567;
      for (let i = 0; i < d.length; i++) {
        seed = (seed * 1103515245 + 12345) & 0x7fffffff;
        d[i] = (seed / 0x7fffffff) * 2 - 1;
      }
      this.ctx = ctx;
      this.master = master;
      this.noiseBuf = buf;
      return ctx;
    } catch {
      return null;
    }
  }

  private noise(): AudioBufferSourceNode {
    const ctx = this.ctx!;
    const n = ctx.createBufferSource();
    n.buffer = this.noiseBuf;
    n.loop = true;
    n.loopStart = Math.random();
    n.start(0, Math.random() * 1.5);
    return n;
  }

  // ------------------------------------------------------------------ engine

  /** The engine of the driven car: the starter, then the engine catches (`cls` picks its voice). */
  startEngine(cls: string, idle = 800): void {
    if (!this.live) return;
    if (this.engine) return;
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    const type = ENGINES[cls] ?? ENGINES["sedan"]!;
    const harm = engineHarmonics(type);
    const wave = ctx.createPeriodicWave(new Float32Array(harm.length), harm, {
      disableNormalization: false,
    });
    const osc = ctx.createOscillator();
    osc.setPeriodicWave(wave);
    const sub = ctx.createOscillator();
    sub.type = "triangle";
    const tone = ctx.createGain();
    const rumble = ctx.createGain();
    const grit = ctx.createGain();
    tone.gain.value = 0;
    rumble.gain.value = 0;
    grit.gain.value = 0;
    const noise = this.noise();
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 1.4;
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(1024);
    for (let i = 0; i < curve.length; i++) {
      const x = (i / (curve.length - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * type.drive) / Math.tanh(type.drive);
    }
    shaper.curve = curve;
    shaper.oversample = "2x";
    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = 400;
    const flutter = ctx.createGain();
    flutter.gain.value = 1;
    const flutterLfo = ctx.createOscillator();
    flutterLfo.type = "square";
    flutterLfo.frequency.value = 17;
    const flutterDepth = ctx.createGain();
    flutterDepth.gain.value = 0;
    flutterLfo.connect(flutterDepth).connect(flutter.gain);
    const voice = ctx.createGain();
    voice.gain.value = 0;
    // A slow wander of the pitch: a real engine is never perfectly steady.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 5.3;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 7;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.detune);
    lfoGain.connect(sub.detune);
    osc.connect(tone).connect(shaper);
    sub.connect(rumble).connect(shaper);
    noise.connect(band).connect(grit).connect(shaper);
    shaper.connect(low).connect(flutter).connect(voice).connect(this.master!);
    // Tyres and wind.
    const squealSrc = this.noise();
    const squealBand = ctx.createBiquadFilter();
    squealBand.type = "bandpass";
    squealBand.frequency.value = 1500;
    squealBand.Q.value = 9;
    const squeal = ctx.createGain();
    squeal.gain.value = 0;
    squealSrc.connect(squealBand).connect(squeal).connect(this.master!);
    const windSrc = this.noise();
    const windLow = ctx.createBiquadFilter();
    windLow.type = "lowpass";
    windLow.frequency.value = 300;
    const wind = ctx.createGain();
    wind.gain.value = 0;
    windSrc.connect(windLow).connect(wind).connect(this.master!);
    for (const o of [osc, sub, lfo, flutterLfo]) o.start();
    const f0 = (idle / 60) * (type.cylinders / 2);
    // Starter: the engine turns slowly for a moment, then catches with a flare.
    osc.frequency.setValueAtTime(f0 * 0.2, now);
    sub.frequency.setValueAtTime(f0 * 0.1, now);
    osc.frequency.setTargetAtTime(f0 * 0.25, now, 0.2);
    voice.gain.setTargetAtTime(0.18, now, 0.05);
    tone.gain.setTargetAtTime(0.5, now, 0.05);
    low.frequency.setTargetAtTime(500, now, 0.05);
    this.starterChug(now, 0.65);
    osc.frequency.setTargetAtTime(f0 * 1.7, now + 0.65, 0.08);
    sub.frequency.setTargetAtTime((f0 * 1.7) / 2, now + 0.65, 0.08);
    osc.frequency.setTargetAtTime(f0, now + 0.95, 0.25);
    sub.frequency.setTargetAtTime(f0 / 2, now + 0.95, 0.25);
    voice.gain.setTargetAtTime(0.4, now + 0.65, 0.06);
    this.engine = {
      type,
      osc,
      sub,
      noise,
      band,
      tone,
      rumble,
      grit,
      shaper,
      low,
      voice,
      flutter,
      flutterLfo,
      flutterDepth,
      lfo,
      squealSrc,
      squealBand,
      squeal,
      windSrc,
      windLow,
      wind,
      shifts: -1,
      started: now + 1.2,
      indicatorOn: false,
      tick: 0,
      dipUntil: 0,
    };
  }

  /** The starter motor: a rhythmic whir under the turning engine. */
  private starterChug(at: number, dur: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.value = 210;
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 900;
    bp.Q.value = 2;
    const g = ctx.createGain();
    g.gain.value = 0;
    const am = ctx.createOscillator();
    am.frequency.value = 11;
    const amg = ctx.createGain();
    amg.gain.value = 0.05;
    am.connect(amg).connect(g.gain);
    o.connect(bp).connect(g).connect(this.master!);
    g.gain.setTargetAtTime(0.07, at, 0.03);
    g.gain.setTargetAtTime(0, at + dur, 0.05);
    o.start(at);
    am.start(at);
    o.stop(at + dur + 0.5);
    am.stop(at + dur + 0.5);
  }

  /** Per frame, while driving. */
  drive(s: DriveSound): void {
    if (!this.live) return;
    if (!this.engine) this.startEngine(s.cls, s.idle);
    const e = this.engine;
    if (!e) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    if (t < e.started) return;
    const v = engineVoice(e.type, s.rpm, s.throttle, s.idle, s.redline);
    e.osc.frequency.setTargetAtTime(v.fire, t, 0.035);
    e.sub.frequency.setTargetAtTime(v.half, t, 0.035);
    e.band.frequency.setTargetAtTime(v.noiseHz, t, 0.05);
    e.tone.gain.setTargetAtTime(v.tone, t, TC);
    e.rumble.gain.setTargetAtTime(v.rumble, t, TC);
    e.grit.gain.setTargetAtTime(v.noise, t, TC);
    e.low.frequency.setTargetAtTime(v.cutoff, t, 0.06);
    e.low.Q.setTargetAtTime(v.q, t, 0.06);
    e.voice.gain.setTargetAtTime(v.level * 0.55 * (t < e.dipUntil ? 0.45 : 1), t, TC);
    // Rev limiter: the engine stutters against it.
    e.flutterDepth.gain.setTargetAtTime(
      s.rpm > s.redline * 1.02 && s.throttle > 0.5 ? 0.45 : 0,
      t,
      0.02,
    );
    if (e.shifts >= 0 && s.shifts !== e.shifts) this.shiftClick(t);
    e.shifts = s.shifts;
    const squeal = Math.max(0, Math.min(1, (s.slide - 0.35) * 1.3)) * Math.min(1, s.speed / 6);
    e.squeal.gain.setTargetAtTime(s.grass ? 0 : squeal * 0.16, t, 0.05);
    e.squealBand.frequency.setTargetAtTime(1300 + Math.random() * 500 + s.speed * 6, t, 0.04);
    const w = Math.min(1, s.speed / 50);
    e.wind.gain.setTargetAtTime(
      w * w * 0.22 + (s.grass ? Math.min(0.08, s.speed / 200) : 0),
      t,
      0.1,
    );
    e.windLow.frequency.setTargetAtTime(250 + s.speed * 28, t, 0.1);
    if (s.impact > 1.6) this.thump(Math.min(1, s.impact / 10));
    // Indicator relay.
    if (s.indicator) {
      if (t > e.tick) {
        e.tick = t + 0.38;
        e.indicatorOn = !e.indicatorOn;
        this.click(e.indicatorOn ? 3200 : 2400, 0.05);
      }
    } else e.tick = 0;
  }

  stopEngine(): void {
    const e = this.engine;
    this.engine = null;
    this.hornOff();
    if (!e || !this.ctx) return;
    const t = this.ctx.currentTime;
    e.osc.frequency.setTargetAtTime(e.osc.frequency.value * 0.3, t, 0.25);
    e.voice.gain.setTargetAtTime(0, t, 0.12);
    e.squeal.gain.setTargetAtTime(0, t, 0.05);
    e.wind.gain.setTargetAtTime(0, t, 0.1);
    const end = t + 0.9;
    for (const o of [e.osc, e.sub, e.lfo, e.flutterLfo, e.noise, e.squealSrc, e.windSrc]) {
      try {
        o.stop(end);
      } catch {
        // Already stopped.
      }
    }
  }

  private shiftClick(t: number): void {
    const e = this.engine;
    // The drive is cut for a moment: the voice dips.
    if (e) e.dipUntil = t + 0.14;
    this.click(1800, 0.03, 0.08);
  }

  /** A short filtered noise burst: relay ticks, latches, the gear lever. */
  click(hz: number, gain: number, dur = 0.025): void {
    if (!this.live) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const n = this.noise();
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = hz;
    bp.Q.value = 3;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    n.connect(bp).connect(g).connect(this.master!);
    n.stop(t + dur + 0.05);
  }

  /** A body thump (doors closing, impacts). */
  thump(strength: number): void {
    if (!this.live) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.setValueAtTime(95, t);
    o.frequency.exponentialRampToValueAtTime(48, t + 0.16);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.5 * strength, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + 0.3);
    const n = this.noise();
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1400;
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0, t);
    ng.gain.linearRampToValueAtTime(0.22 * strength, t + 0.003);
    ng.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    n.connect(lp).connect(ng).connect(this.master!);
    n.stop(t + 0.15);
  }

  /** Door: the latch, a soft creak when opening; latch and thump when closing. */
  door(open: boolean): void {
    if (!this.live) return;
    this.click(open ? 2600 : 2100, 0.12, 0.04);
    if (!open) {
      this.thump(0.85);
      return;
    }
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const n = this.noise();
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 12;
    bp.frequency.setValueAtTime(700, t + 0.05);
    bp.frequency.linearRampToValueAtTime(1100, t + 0.45);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.05, t + 0.1);
    g.gain.linearRampToValueAtTime(0, t + 0.5);
    n.connect(bp).connect(g).connect(this.master!);
    n.stop(t + 0.6);
  }

  hornOn(): void {
    if (!this.live || this.horn) return;
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const a = ctx.createOscillator();
    const b = ctx.createOscillator();
    a.type = "square";
    b.type = "square";
    a.frequency.value = 415;
    b.frequency.value = 523;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2200;
    const g = ctx.createGain();
    g.gain.value = 0;
    a.connect(lp);
    b.connect(lp);
    lp.connect(g).connect(this.master!);
    g.gain.setTargetAtTime(0.11, t, 0.012);
    a.start(t);
    b.start(t);
    this.horn = { a, b, g };
  }

  hornOff(): void {
    const h = this.horn;
    this.horn = null;
    if (!h || !this.ctx) return;
    const t = this.ctx.currentTime;
    h.g.gain.setTargetAtTime(0, t, 0.02);
    h.a.stop(t + 0.2);
    h.b.stop(t + 0.2);
  }

  // ------------------------------------------------------------------ other drivers

  /** 3D voices for other drivers' cars near the listener (the three nearest). */
  remote(
    list: readonly RemoteSound[],
    listener: { x: number; y: number; z: number; fx: number; fy: number; fz: number },
  ): void {
    if (!this.live) {
      if (this.remotes.size) this.clearRemotes();
      return;
    }
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const L = ctx.listener;
    if (L.positionX) {
      L.positionX.setTargetAtTime(listener.x, t, 0.05);
      L.positionY.setTargetAtTime(listener.y, t, 0.05);
      L.positionZ.setTargetAtTime(listener.z, t, 0.05);
      L.forwardX.setTargetAtTime(listener.fx, t, 0.05);
      L.forwardY.setTargetAtTime(listener.fy, t, 0.05);
      L.forwardZ.setTargetAtTime(listener.fz, t, 0.05);
    }
    const near = [...list]
      .map((r) => ({ r, d: Math.hypot(r.x - listener.x, r.z - listener.z) }))
      .filter((x) => x.d < 45)
      .sort((a, b) => a.d - b.d)
      .slice(0, 3);
    const keep = new Set(near.map((n) => n.r.id));
    for (const [id, v] of this.remotes) if (!keep.has(id)) this.dropRemote(id, v);
    for (const { r } of near) {
      let v = this.remotes.get(r.id);
      if (!v) {
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        const low = ctx.createBiquadFilter();
        low.type = "lowpass";
        const gain = ctx.createGain();
        gain.gain.value = 0;
        const panner = ctx.createPanner();
        panner.panningModel = "equalpower";
        panner.distanceModel = "inverse";
        panner.refDistance = 4;
        panner.rolloffFactor = 1.3;
        osc.connect(low).connect(gain).connect(panner).connect(this.master!);
        osc.start();
        v = { osc, low, gain, panner, seen: t };
        this.remotes.set(r.id, v);
      }
      const type = ENGINES[r.cls] ?? ENGINES["sedan"]!;
      const rpm = 900 + Math.min(1, Math.abs(r.speed) / 30) * 3800;
      const voice = engineVoice(type, rpm, Math.min(1, Math.abs(r.speed) / 15), 900, 6000);
      v.osc.frequency.setTargetAtTime(voice.fire, t, 0.08);
      v.low.frequency.setTargetAtTime(voice.cutoff * 0.6, t, 0.08);
      v.gain.gain.setTargetAtTime(type.cylinders <= 1 ? 0 : 0.1 + voice.level * 0.12, t, 0.1);
      if (v.panner.positionX) {
        v.panner.positionX.setTargetAtTime(r.x, t, 0.05);
        v.panner.positionY.setTargetAtTime(r.y + 0.6, t, 0.05);
        v.panner.positionZ.setTargetAtTime(r.z, t, 0.05);
      }
      v.seen = t;
    }
  }

  private dropRemote(id: string, v: RemoteVoice): void {
    const t = this.ctx?.currentTime ?? 0;
    v.gain.gain.setTargetAtTime(0, t, 0.08);
    v.osc.stop(t + 0.5);
    this.remotes.delete(id);
  }

  private clearRemotes(): void {
    for (const [id, v] of this.remotes) this.dropRemote(id, v);
  }

  /** Test hook: what is playing. */
  status(): {
    context: string;
    engine: boolean;
    remotes: number;
    enabled: boolean;
    volume: number;
  } {
    return {
      context: this.ctx?.state ?? "none",
      engine: this.engine !== null,
      remotes: this.remotes.size,
      enabled: this.settings.enabled,
      volume: this.settings.volume,
    };
  }
}

export const vehicleAudio = new VehicleAudio();
